/**
 * Shared types + pure helpers for referrer patient UI (API + demo).
 * personKey ≈ document_type|NormalizePatientIDForGrouping(document_number).
 */

export type ReferrerPatientSex = 'F' | 'M' | 'O';
export type ReferrerPatientStatus = 'ready' | 'in_process' | 'overdue' | 'new';

/** Display label for patient sex codes (API: M | F | O). */
export function formatPatientSex(sex: ReferrerPatientSex | string | null | undefined): string {
  switch ((sex || '').toString().trim().toUpperCase()) {
    case 'F':
      return 'Femenino';
    case 'M':
      return 'Masculino';
    case 'O':
      return 'Otro';
    default:
      return 'No definido';
  }
}
export type ReferrerStudyTone = 'teal' | 'amber' | 'red' | 'green' | 'blue';
export type ReferrerStudyKind = 'lab' | 'imaging';
export type ReferrerReportOrigin = 'native' | 'third_party';

export interface ReferrerProfileSite {
  id: string;
  nodeId: string;
  label: string;
  status: 'granted' | 'blocked';
  color?: string;
  /** Monitor template: can list patients for this node. Undefined = treat as allowed. */
  viewPatients?: boolean;
}

export interface ReferrerSiteChip {
  siteId: string;
  label: string;
  color?: string;
}

export interface ReferrerSiteRecord {
  siteId: string;
  siteName: string;
  hc: string;
  /** Portal patients.id for this sede row (MONITOR study fetch). */
  patientId?: number;
}

export interface ReferrerStudyStep {
  label: string;
  done: boolean;
  current?: boolean;
}

export interface ReferrerStudyReport {
  id: string;
  title: string;
  physicianName: string;
  physicianSerial: string;
  status: 'draft' | 'completed';
  origin: ReferrerReportOrigin;
  /**
   * Instantánea del informe (firma o creación).
   * Preferir ISO; la UI formatea según idioma (ES=12h, EN=24h).
   */
  signedAt?: string;
  findings: string;
  conclusion: string;
  /** Absolute or /api/files URL when API provides pdf_url / pdf_download_url / url_author. */
  pdfUrl?: string;
}

/**
 * Fecha + hora del informe según idioma de la app:
 * - ES → 12 horas (a. m. / p. m.)
 * - EN/US → 24 horas
 */
export function formatReportDateTime(
  iso?: string | null,
  locale: string = 'es',
): string {
  const raw = (iso || '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;

  const isEn = locale.toLowerCase().startsWith('en');
  const tag = isEn ? 'en-US' : 'es-DO';
  const hour12 = !isEn;

  const date = d.toLocaleDateString(tag, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString(tag, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  });
  return `${date} · ${time}`;
}

export interface ReferrerPatientStudy {
  id: string;
  kind: ReferrerStudyKind;
  site: string;
  siteId: string;
  name: string;
  orderCode: string;
  modality: string;
  requestedAt: string;
  progress: number;
  tone: ReferrerStudyTone;
  statusLabel: string;
  steps: ReferrerStudyStep[];
  reports: ReferrerStudyReport[];
  /** Demo-only thumbnail URLs; production imaging uses GET .../viewers → urls.ohif. */
  imageUrls?: string[];
  arrivedAt?: string;
  /** Assigned radiologist(s) / taken_by — display string. */
  radiologistLabel?: string;
  /** Referring physician + REFERRER assignments — display string. */
  referrerLabel?: string;
}

export interface ReferrerClinicalNote {
  id: string;
  author: string;
  role: string;
  site: string;
  at: string;
  body: string;
}

export interface ReferrerPersonCard {
  personKey: string;
  name: string;
  documentType: string;
  documentNumber: string;
  documentNumberDisplay: string;
  sex: ReferrerPatientSex;
  /** Years; null when birth date / age string is missing or not trustworthy. */
  age: number | null;
  phone: string;
  photoUrl: string;
  initials: string;
  accent: string;
  recordsBySite: ReferrerSiteRecord[];
  siteIdsActive: string[];
  assignmentCount: number;
  sitesActive: number;
  sitesRegistered: number;
  primaryHc: string;
  sitesSummary: string;
  siteChips: ReferrerSiteChip[];
  lastStudy: string;
  lastVisit: string;
  /** ISO timestamp of latest study/activity; used for “hoy” default filter. */
  lastActivityAt?: string;
  status: ReferrerPatientStatus;
  statusLabel: string;
  starred: boolean;
  recent: boolean;
  /** Populated from API (or demo) for the detail modal. */
  studies: ReferrerPatientStudy[];
  notes: ReferrerClinicalNote[];
}

export interface ReferrerSiteBucket {
  siteId: string;
  nodeId: string;
  siteName: string;
  status: 'granted' | 'blocked';
  color?: string;
  hc?: string;
  studies: ReferrerPatientStudy[];
}

const STATUS_LABEL: Record<ReferrerPatientStatus, string> = {
  overdue: 'Atrasado',
  new: 'Nuevo',
  in_process: 'En proceso',
  ready: 'Resultado listo',
};

const STATUS_PRIORITY: ReferrerPatientStatus[] = ['overdue', 'new', 'in_process', 'ready'];

export function statusLabelFor(status: ReferrerPatientStatus): string {
  return STATUS_LABEL[status];
}

export function rollupStatus(statuses: ReferrerPatientStatus[]): ReferrerPatientStatus {
  for (const s of STATUS_PRIORITY) {
    if (statuses.includes(s)) return s;
  }
  return 'ready';
}

/** Same rules as API NormalizePatientIDForGrouping (no hyphens or whitespace). */
export function normalizePatientIdForGrouping(patientId: string): string {
  const stripNoise = (value: string) => value.replace(/[-\s]+/g, '');
  const raw = patientId.trim();
  if (!raw) return '';
  const idx = raw.indexOf('_');
  if (idx >= 0) {
    const parts = raw.split('_');
    if (parts.length >= 2) {
      const cedula = parts[1].trim();
      if (cedula) return stripNoise(cedula);
    }
  }
  return stripNoise(raw);
}

export function personKeyFromDocument(documentType: string, documentNumber: string): string {
  const type = documentType.trim().toUpperCase() || 'CEDULA';
  const digits = normalizePatientIdForGrouping(documentNumber);
  return `${type}|${digits}`;
}

export function formatDocumentDisplay(documentNumber: string): string {
  const raw = documentNumber.trim();
  if (!raw) return '—';
  if (raw.includes('-')) return raw;
  // Light formatting for Dominican cédula-like 11 digits: 000-0000000-0
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10)}`;
  }
  return raw;
}

export function buildSiteBuckets(
  person: ReferrerPersonCard,
  profileSites: ReferrerProfileSite[],
): ReferrerSiteBucket[] {
  const studies = person.studies ?? [];
  return profileSites
    .filter((s) => s.status === 'granted')
    .map((site) => {
      const record = person.recordsBySite.find(
        (r) =>
          r.siteId === site.id ||
          r.siteId.toLowerCase() === site.id.toLowerCase() ||
          r.siteId.toLowerCase() === site.nodeId.toLowerCase(),
      );
      return {
        siteId: site.id,
        nodeId: site.nodeId,
        siteName: site.label,
        status: site.status,
        color: site.color,
        hc: record?.hc,
        studies: studies.filter((st) => {
          const id = (st.siteId || '').toLowerCase();
          return id === site.id.toLowerCase() || id === site.nodeId.toLowerCase();
        }),
      };
    });
}


/** True when `iso` falls on the same local calendar day as `ref`. */
export function isSameLocalCalendarDay(iso?: string | null, ref: Date = new Date()): boolean {
  if (!iso) return false;
  const raw = String(iso).trim();
  if (!raw) return false;

  let d: Date | null = null;
  if (/^\d{8}$/.test(raw)) {
    // DICOM / compact YYYYMMDD
    d = new Date(Number(raw.slice(0, 4)), Number(raw.slice(4, 6)) - 1, Number(raw.slice(6, 8)));
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, day] = raw.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }
  if (!d || Number.isNaN(d.getTime())) return false;

  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

/** Patient has a study/activity dated today (local). */
export function personHasActivityToday(person: ReferrerPersonCard, ref: Date = new Date()): boolean {
  if (isSameLocalCalendarDay(person.lastActivityAt, ref)) return true;
  return (person.studies ?? []).some((s) => isSameLocalCalendarDay(s.arrivedAt, ref));
}

export function personMatchesSiteFilter(person: ReferrerPersonCard, siteId: string): boolean {
  if (siteId === 'all') return true;
  const want = siteId.trim().toLowerCase();
  if (!want) return true;
  return (
    person.siteIdsActive.some((id) => id.toLowerCase() === want) ||
    person.recordsBySite.some((r) => r.siteId.toLowerCase() === want) ||
    (person.studies ?? []).some((s) => (s.siteId || '').toLowerCase() === want)
  );
}

export function personMatchesSearch(person: ReferrerPersonCard, rawQ: string): boolean {
  const q = rawQ.trim().toLowerCase();
  if (!q) return true;
  const qDigits = q.replace(/-/g, '');
  if (
    person.name.toLowerCase().includes(q) ||
    person.phone.toLowerCase().includes(q) ||
    person.primaryHc.toLowerCase().includes(q) ||
    person.documentNumberDisplay.toLowerCase().includes(q) ||
    person.documentNumber.includes(qDigits) ||
    person.lastStudy.toLowerCase().includes(q) ||
    person.sitesSummary.toLowerCase().includes(q)
  ) {
    return true;
  }
  if (person.recordsBySite.some((r) => r.hc.toLowerCase().includes(q) || r.siteName.toLowerCase().includes(q))) {
    return true;
  }
  return (person.studies ?? []).some(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.orderCode.toLowerCase().includes(q) ||
      s.site.toLowerCase().includes(q),
  );
}

export function personMatchesQuickFilter(
  person: ReferrerPersonCard,
  filter: 'all' | 'ready' | 'in_process' | 'recent' | 'starred',
): boolean {
  if (filter === 'all') return true;
  if (filter === 'recent') return person.recent;
  if (filter === 'starred') return person.starred;
  if (filter === 'ready') {
    return person.status === 'ready' || (person.studies ?? []).some((s) => s.progress >= 4);
  }
  if (filter === 'in_process') {
    return (
      person.status === 'in_process' ||
      (person.studies ?? []).some((s) => s.progress > 0 && s.progress < 4)
    );
  }
  return true;
}

export function sitesSummaryLabel(activeLabels: string[]): string {
  if (activeLabels.length === 0) return 'Sin sedes con asignación';
  if (activeLabels.length === 1) return activeLabels[0];
  if (activeLabels.length === 2) return `${activeLabels[0]} · ${activeLabels[1]}`;
  return `${activeLabels[0]} +${activeLabels.length - 1} sedes`;
}

const ACCENTS = ['#0d9488', '#ea580c', '#dc2626', '#2563eb', '#7c3aed', '#0891b2', '#ca8a04'];

export function accentFromKey(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
